import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, Plus, Minus, Trash2, Loader2, ShoppingBag, X, CheckCircle,
  Banknote, CreditCard, Smartphone, Flower2, Package, History, ArrowLeft
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => `Rp ${Number(v || 0).toLocaleString("id-ID")}`;

const PAY_METHODS = [
  { id: "cash", label: "Tunai", icon: Banknote },
  { id: "card", label: "Kartu/EDC", icon: CreditCard },
  { id: "transfer", label: "Transfer", icon: Smartphone },
];

export default function AdminPOS() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("catalog"); // "catalog" | "custom" | "checkout" | "history"
  const [payMethod, setPayMethod] = useState("cash");
  const [discount, setDiscount] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [payReceived, setPayReceived] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lastReceipt, setLastReceipt] = useState(null);
  const [orders, setOrders] = useState([]);
  const [customItem, setCustomItem] = useState({ name: "Custom Bouquet", description: "", qty: 1, unit_price: "" });
  const [customMode, setCustomMode] = useState("text"); // "text" | "structured"
  const [flowersInventory, setFlowersInventory] = useState([]);
  const [selectedFlowers, setSelectedFlowers] = useState([]); // [{flower_code, flower_name, qty}]

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, oRes, fRes] = await Promise.all([
        axios.get(`${API}/products`).then(r => r.data).catch(() => ({ products: [] })),
        axios.get(`${API}/admin/pos/orders`, { withCredentials: true }).then(r => r.data).catch(() => ({ orders: [] })),
        axios.get(`${API}/admin/inventory/flowers`, { withCredentials: true }).then(r => r.data).catch(() => ({ flowers: [] }))
      ]);
      setProducts(pRes.products || pRes || []);
      setOrders(oRes.orders || []);
      setFlowersInventory(fRes.flowers || []);
    } catch {
      toast.error("Gagal memuat produk");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(i => i.product_id === product.product_id);
      if (existing) return prev.map(i => i.product_id === product.product_id ? { ...i, qty: i.qty + 1, subtotal: (i.qty + 1) * i.unit_price } : i);
      return [...prev, {
        type: "product",
        product_id: product.product_id,
        name: product.title || product.name,
        unit_price: product.price || 0,
        qty: 1,
        subtotal: product.price || 0
      }];
    });
  };

  const addCustom = () => {
    if (!customItem.name || !customItem.unit_price) { toast.error("Nama dan harga wajib diisi"); return; }
    
    const price = parseFloat(customItem.unit_price) || 0;
    const cartItem = {
      type: "custom",
      name: customItem.name,
      description: customItem.description,
      qty: parseInt(customItem.qty) || 1,
      unit_price: price,
      subtotal: price * (parseInt(customItem.qty) || 1)
    };
    
    // Add flowers_used if structured mode
    if (customMode === "structured" && selectedFlowers.length > 0) {
      cartItem.flowers_used = selectedFlowers.map(f => ({
        flower_code: f.flower_code,
        flower_name: f.flower_name,
        qty: parseFloat(f.qty) || 0
      }));
    }
    
    setCart(prev => [...prev, cartItem]);
    setCustomItem({ name: "Custom Bouquet", description: "", qty: 1, unit_price: "" });
    setSelectedFlowers([]);
    setCustomMode("text");
    setMode("catalog");
    toast.success("Item custom ditambahkan ke keranjang");
  };

  const updateQty = (idx, delta) => {
    setCart(prev => {
      const updated = [...prev];
      const newQty = updated[idx].qty + delta;
      if (newQty <= 0) return prev.filter((_, i) => i !== idx);
      updated[idx] = { ...updated[idx], qty: newQty, subtotal: newQty * updated[idx].unit_price };
      return updated;
    });
  };

  const removeItem = (idx) => setCart(prev => prev.filter((_, i) => i !== idx));

  const subtotal = cart.reduce((s, i) => s + i.subtotal, 0);
  const disc = parseFloat(discount) || 0;
  const total = Math.max(0, subtotal - disc);
  const received = parseFloat(payReceived) || total;
  const change = Math.max(0, received - total);

  const submitOrder = async () => {
    if (!cart.length) { toast.error("Keranjang kosong"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/pos/orders`, {
        items: cart,
        customer_name: customerName || "Walk-in",
        customer_phone: customerPhone,
        discount: disc,
        payment_method: payMethod,
        payment_received: received,
        notes: ""
      }, { withCredentials: true });
      
      // Check for stock warnings
      if (res.data.stock_warnings && res.data.stock_warnings.length > 0) {
        const warnings = res.data.stock_warnings;
        const warningMsg = warnings.map(w => 
          `${w.flower_name}: butuh ${w.required}, tersedia ${w.available} (kurang ${w.shortage})`
        ).join("\n");
        toast.warning(`⚠️ Stok tidak cukup (order tetap diproses):\n${warningMsg}`, { duration: 8000 });
      }
      
      setLastReceipt(res.data);
      setCart([]);
      setDiscount("");
      setCustomerName("");
      setCustomerPhone("");
      setPayReceived("");
      await load();
      toast.success("Transaksi berhasil!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal membuat order");
    }
    setSubmitting(false);
  };

  const filteredProducts = products.filter(p =>
    !search || (p.title || p.name || "").toLowerCase().includes(search.toLowerCase())
  );

  if (lastReceipt) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
        className="max-w-md mx-auto py-10" data-testid="pos-receipt">
        <div className="bg-white rounded-2xl border border-[#D4859B]/20 shadow-xl p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-green-600" />
          </div>
          <h2 className="font-heading text-3xl text-[#1C1A19] mb-1">Transaksi <em className="text-seikat-pink">Berhasil!</em></h2>
          <p className="font-body text-sm text-[#8A857D] mb-6">{lastReceipt.pos_order_id}</p>
          <div className="bg-[#FDF8F6] rounded-xl p-4 text-left space-y-2 mb-6">
            {lastReceipt.items?.map((item, i) => (
              <div key={i} className="flex justify-between text-sm font-body">
                <span className="text-[#4A453F]">{item.name} x{item.qty}</span>
                <span className="text-[#1C1A19] font-medium">{fmt(item.subtotal)}</span>
              </div>
            ))}
            <div className="border-t border-[#D4859B]/15 pt-2 mt-2">
              {lastReceipt.discount > 0 && (
                <div className="flex justify-between text-sm font-body text-seikat-pink">
                  <span>Diskon</span><span>-{fmt(lastReceipt.discount)}</span>
                </div>
              )}
              <div className="flex justify-between text-base font-body font-bold text-[#1C1A19]">
                <span>Total</span><span>{fmt(lastReceipt.total)}</span>
              </div>
              {lastReceipt.payment_method === "cash" && (
                <>
                  <div className="flex justify-between text-sm font-body text-[#6A6560]">
                    <span>Bayar</span><span>{fmt(lastReceipt.payment_received)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-body text-green-700 font-semibold">
                    <span>Kembalian</span><span>{fmt(lastReceipt.change)}</span>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setLastReceipt(null)}
              className="flex-1 px-4 py-2.5 bg-seikat-pink text-white rounded-xl text-sm font-body font-medium hover:bg-seikat-pink/90 transition-colors">
              Transaksi Baru
            </button>
            <button onClick={() => { setLastReceipt(null); setMode("history"); }}
              className="flex-1 px-4 py-2.5 border border-[#D4859B]/20 rounded-xl text-sm font-body text-[#4A453F] hover:bg-[#FDF8F6] transition-colors">
              Lihat Riwayat
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <div data-testid="admin-pos">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl text-[#1C1A19]">POS <span className="text-seikat-pink">Offline</span></h1>
          <p className="font-body text-sm text-[#8A857D] mt-1">Transaksi penjualan langsung di toko</p>
        </div>
        <div className="flex gap-1 p-1 bg-[#FDF8F6] border border-[#D4859B]/15 rounded-2xl">
          {[
            { id: "catalog", label: "Katalog", icon: Package },
            { id: "custom", label: "Custom Bouquet", icon: Flower2 },
            { id: "history", label: "Riwayat", icon: History },
          ].map(t => (
            <button key={t.id} onClick={() => setMode(t.id)} data-testid={`pos-tab-${t.id}`}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-body transition-all ${mode === t.id ? "bg-seikat-pink text-white shadow-sm" : "text-[#4A453F] hover:text-[#1C1A19]"}`}>
              <t.icon size={13} />{t.label}
            </button>
          ))}
        </div>
      </div>

      {mode === "history" ? (
        <POSHistory orders={orders} />
      ) : (
        <div className="grid lg:grid-cols-[1fr_380px] gap-6">
          {/* Left: Product catalog or custom input */}
          <div>
            {mode === "catalog" ? (
              <>
                <div className="relative mb-4">
                  <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#A8A29A]" />
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cari produk..."
                    data-testid="pos-search"
                    className="w-full pl-9 pr-4 py-2.5 bg-white border border-[#D4859B]/20 rounded-xl text-sm font-body outline-none focus:border-seikat-pink/50" />
                </div>
                {loading ? (
                  <div className="flex justify-center py-16"><Loader2 className="animate-spin text-seikat-pink" size={28} /></div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="pos-product-grid">
                    {filteredProducts.map(p => (
                      <motion.button key={p.product_id} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                        onClick={() => addToCart(p)} data-testid={`pos-product-${p.product_id}`}
                        className="bg-white border border-[#D4859B]/15 rounded-xl p-3 text-left hover:border-seikat-pink/40 hover:shadow-sm transition-all">
                        {p.images?.[0] ? (
                          <img src={p.images[0]} alt={p.title} className="w-full h-28 object-cover rounded-lg mb-2" />
                        ) : (
                          <div className="w-full h-28 bg-seikat-pink/10 rounded-lg mb-2 flex items-center justify-center">
                            <Flower2 size={24} className="text-seikat-pink/40" />
                          </div>
                        )}
                        <p className="font-body text-sm font-medium text-[#1C1A19] truncate">{p.title || p.name}</p>
                        <p className="font-body text-xs text-seikat-pink font-semibold mt-0.5">{fmt(p.price)}</p>
                      </motion.button>
                    ))}
                    {filteredProducts.length === 0 && (
                      <div className="col-span-3 text-center py-16 font-body text-[#A8A29A]">
                        <Package size={28} className="mx-auto mb-2 text-[#D4859B]/30" /><p>Tidak ada produk</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              /* Custom bouquet form */
              <div className="bg-white rounded-2xl border border-[#D4859B]/15 shadow-sm p-6 max-w-2xl">
                <h2 className="font-heading text-xl text-[#1C1A19] mb-5">Input <em className="text-seikat-pink">Custom Bouquet</em></h2>
                
                {/* Toggle mode: Text atau Structured */}
                <div className="flex gap-2 mb-5 bg-[#FDF8F6] p-1 rounded-xl">
                  <button onClick={() => setCustomMode("text")} data-testid="custom-mode-text"
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-body transition-colors ${
                      customMode === "text" ? "bg-white text-seikat-pink shadow-sm" : "text-[#8A857D] hover:text-[#1C1A19]"
                    }`}>
                    📝 Text Bebas
                  </button>
                  <button onClick={() => setCustomMode("structured")} data-testid="custom-mode-structured"
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-body transition-colors ${
                      customMode === "structured" ? "bg-white text-seikat-pink shadow-sm" : "text-[#8A857D] hover:text-[#1C1A19]"
                    }`}>
                    🌸 Pilih Bunga (Deduct Inventory)
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-[10px] tracking-widest uppercase text-[#8A857D] mb-1.5">Nama Item</label>
                    <input value={customItem.name} onChange={e => setCustomItem({ ...customItem, name: e.target.value })}
                      placeholder="Custom Bouquet Mawar Merah" data-testid="custom-item-name"
                      className="w-full px-3 py-2.5 bg-[#FDF8F6] border border-[#D4859B]/20 rounded-xl text-sm font-body outline-none focus:border-seikat-pink/50" />
                  </div>

                  {customMode === "text" ? (
                    // Text Mode: Free description
                    <div>
                      <label className="block text-[10px] tracking-widest uppercase text-[#8A857D] mb-1.5">Deskripsi / Komposisi Bunga</label>
                      <textarea rows={3} value={customItem.description} onChange={e => setCustomItem({ ...customItem, description: e.target.value })}
                        placeholder="12 mawar merah, 3 baby breath, pita sutra putih..." data-testid="custom-item-desc"
                        className="w-full px-3 py-2.5 bg-[#FDF8F6] border border-[#D4859B]/20 rounded-xl text-sm font-body outline-none focus:border-seikat-pink/50 resize-none" />
                      <p className="text-[10px] text-[#A8A29A] mt-1">⚠️ Mode text: Inventory tidak akan berkurang otomatis</p>
                    </div>
                  ) : (
                    // Structured Mode: Flower Picker
                    <div>
                      <label className="block text-[10px] tracking-widest uppercase text-[#8A857D] mb-1.5">Pilih Bunga dari Inventory</label>
                      <div className="space-y-2">
                        {selectedFlowers.map((flower, idx) => (
                          <div key={idx} className="flex items-center gap-2 bg-[#FDF8F6] p-2 rounded-xl">
                            <span className="flex-1 text-sm font-body text-[#1C1A19]">{flower.flower_name}</span>
                            <input type="number" min="1" value={flower.qty} 
                              onChange={e => {
                                const updated = [...selectedFlowers];
                                updated[idx].qty = e.target.value;
                                setSelectedFlowers(updated);
                              }}
                              className="w-20 px-2 py-1 border border-[#D4859B]/20 rounded-lg text-sm text-center" />
                            <span className="text-xs text-[#8A857D]">tangkai</span>
                            <button onClick={() => setSelectedFlowers(prev => prev.filter((_, i) => i !== idx))}
                              className="text-red-500 hover:text-red-600">
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                        
                        {/* Dropdown untuk tambah bunga */}
                        <select onChange={e => {
                          const selected = flowersInventory.find(f => f.flower_code === e.target.value);
                          if (selected && !selectedFlowers.find(f => f.flower_code === selected.flower_code)) {
                            setSelectedFlowers(prev => [...prev, {
                              flower_code: selected.flower_code,
                              flower_name: selected.flower_name || selected.flower_code,
                              qty: 1
                            }]);
                          }
                          e.target.value = "";
                        }} data-testid="flower-picker-dropdown"
                          className="w-full px-3 py-2.5 bg-white border border-[#D4859B]/20 rounded-xl text-sm font-body outline-none focus:border-seikat-pink/50">
                          <option value="">+ Tambah Bunga...</option>
                          {flowersInventory.map(f => (
                            <option key={f.flower_code} value={f.flower_code} disabled={selectedFlowers.some(sf => sf.flower_code === f.flower_code)}>
                              {f.flower_name || f.flower_code} ({f.qty} tersedia)
                            </option>
                          ))}
                        </select>
                        <p className="text-[10px] text-green-600">✅ Inventory akan berkurang otomatis sesuai bunga yang dipilih</p>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] tracking-widest uppercase text-[#8A857D] mb-1.5">Harga (Rp) *</label>
                      <input type="number" min="0" value={customItem.unit_price} onChange={e => setCustomItem({ ...customItem, unit_price: e.target.value })}
                        placeholder="150000" data-testid="custom-item-price"
                        className="w-full px-3 py-2.5 bg-[#FDF8F6] border border-[#D4859B]/20 rounded-xl text-sm font-body outline-none focus:border-seikat-pink/50" />
                    </div>
                    <div>
                      <label className="block text-[10px] tracking-widest uppercase text-[#8A857D] mb-1.5">Qty</label>
                      <input type="number" min="1" value={customItem.qty} onChange={e => setCustomItem({ ...customItem, qty: e.target.value })}
                        placeholder="1" data-testid="custom-item-qty"
                        className="w-full px-3 py-2.5 bg-[#FDF8F6] border border-[#D4859B]/20 rounded-xl text-sm font-body outline-none focus:border-seikat-pink/50" />
                    </div>
                  </div>
                  {customItem.unit_price && (
                    <div className="bg-seikat-pink/8 rounded-xl p-3 text-sm font-body text-[#1C1A19]">
                      Total: <span className="font-semibold text-seikat-pink">{fmt(parseFloat(customItem.unit_price || 0) * (parseInt(customItem.qty) || 1))}</span>
                    </div>
                  )}
                  <button onClick={addCustom} data-testid="add-custom-to-cart"
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-seikat-pink text-white rounded-xl text-sm font-body font-medium hover:bg-seikat-pink/90 transition-colors">
                    <Plus size={14} /> Tambah ke Keranjang
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right: Cart + Checkout */}
          <div className="bg-white rounded-2xl border border-[#D4859B]/15 shadow-sm flex flex-col" data-testid="pos-cart">
            <div className="p-5 border-b border-[#D4859B]/12">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-xl text-[#1C1A19]">Keranjang</h3>
                {cart.length > 0 && (
                  <button onClick={() => setCart([])} className="text-xs font-body text-[#A8A29A] hover:text-red-500 transition-colors">Kosongkan</button>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px] max-h-[320px]">
              {cart.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full py-8 text-center">
                  <ShoppingBag size={28} className="text-[#D4859B]/30 mb-2" />
                  <p className="font-body text-sm text-[#A8A29A]">Pilih produk atau tambah item custom</p>
                </div>
              ) : cart.map((item, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-sm font-medium text-[#1C1A19] truncate">{item.name}</p>
                    {item.description && <p className="font-body text-xs text-[#A8A29A] truncate">{item.description}</p>}
                    <p className="font-body text-xs text-seikat-pink">{fmt(item.unit_price)}/item</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => updateQty(idx, -1)} className="w-6 h-6 rounded-full border border-[#D4859B]/30 flex items-center justify-center hover:bg-seikat-pink/10 transition-colors">
                      <Minus size={10} />
                    </button>
                    <span className="w-7 text-center font-body text-sm font-medium">{item.qty}</span>
                    <button onClick={() => updateQty(idx, 1)} className="w-6 h-6 rounded-full border border-[#D4859B]/30 flex items-center justify-center hover:bg-seikat-pink/10 transition-colors">
                      <Plus size={10} />
                    </button>
                  </div>
                  <p className="font-body text-sm font-semibold text-[#1C1A19] w-20 text-right">{fmt(item.subtotal)}</p>
                  <button onClick={() => removeItem(idx)} className="text-[#A8A29A] hover:text-red-500 transition-colors">
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>

            {/* Checkout section */}
            <div className="p-4 border-t border-[#D4859B]/12 space-y-3">
              {/* Customer */}
              <div className="grid grid-cols-2 gap-2">
                <input value={customerName} onChange={e => setCustomerName(e.target.value)}
                  placeholder="Nama pelanggan" data-testid="pos-customer-name"
                  className="px-3 py-2 bg-[#FDF8F6] border border-[#D4859B]/20 rounded-xl text-xs font-body outline-none focus:border-seikat-pink/50" />
                <input value={customerPhone} onChange={e => setCustomerPhone(e.target.value)}
                  placeholder="No. HP" data-testid="pos-customer-phone"
                  className="px-3 py-2 bg-[#FDF8F6] border border-[#D4859B]/20 rounded-xl text-xs font-body outline-none focus:border-seikat-pink/50" />
              </div>

              {/* Discount */}
              <div className="flex items-center gap-2">
                <label className="text-[10px] tracking-widest uppercase text-[#8A857D] whitespace-nowrap">Diskon (Rp)</label>
                <input type="number" min="0" value={discount} onChange={e => setDiscount(e.target.value)}
                  placeholder="0" data-testid="pos-discount"
                  className="flex-1 px-3 py-2 bg-[#FDF8F6] border border-[#D4859B]/20 rounded-xl text-xs font-body outline-none focus:border-seikat-pink/50" />
              </div>

              {/* Payment method */}
              <div className="flex gap-1.5">
                {PAY_METHODS.map(m => (
                  <button key={m.id} onClick={() => setPayMethod(m.id)} data-testid={`pay-method-${m.id}`}
                    className={`flex-1 flex flex-col items-center gap-0.5 py-2 rounded-xl border text-xs font-body transition-all ${payMethod === m.id ? "bg-seikat-pink text-white border-seikat-pink" : "border-[#D4859B]/20 text-[#6A6560] hover:border-seikat-pink/40"}`}>
                    <m.icon size={13} />{m.label}
                  </button>
                ))}
              </div>

              {/* Cash received */}
              {payMethod === "cash" && (
                <div className="flex items-center gap-2">
                  <label className="text-[10px] tracking-widest uppercase text-[#8A857D] whitespace-nowrap">Uang Diterima</label>
                  <input type="number" min="0" value={payReceived} onChange={e => setPayReceived(e.target.value)}
                    placeholder={String(total)} data-testid="pos-pay-received"
                    className="flex-1 px-3 py-2 bg-[#FDF8F6] border border-[#D4859B]/20 rounded-xl text-xs font-body outline-none focus:border-seikat-pink/50" />
                </div>
              )}

              {/* Summary */}
              <div className="bg-[#FDF8F6] rounded-xl p-3 space-y-1.5">
                <div className="flex justify-between text-xs font-body text-[#6A6560]">
                  <span>Subtotal</span><span>{fmt(subtotal)}</span>
                </div>
                {disc > 0 && (
                  <div className="flex justify-between text-xs font-body text-seikat-pink">
                    <span>Diskon</span><span>-{fmt(disc)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm font-body font-bold text-[#1C1A19] border-t border-[#D4859B]/15 pt-1.5">
                  <span>Total</span><span>{fmt(total)}</span>
                </div>
                {payMethod === "cash" && payReceived && (
                  <div className="flex justify-between text-xs font-body text-green-700 font-semibold">
                    <span>Kembalian</span><span>{fmt(change)}</span>
                  </div>
                )}
              </div>

              <button onClick={submitOrder} disabled={submitting || !cart.length} data-testid="pos-submit-order"
                className="w-full flex items-center justify-center gap-2 py-3 bg-seikat-pink text-white rounded-xl text-sm font-body font-semibold hover:bg-seikat-pink/90 transition-colors disabled:opacity-50">
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                Selesaikan Transaksi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function POSHistory({ orders }) {
  const fmt = (v) => `Rp ${Number(v || 0).toLocaleString("id-ID")}`;
  const totalRevenue = orders.reduce((s, o) => s + (o.total || 0), 0);

  return (
    <div data-testid="pos-history">
      <div className="flex items-center gap-4 mb-6 p-4 bg-seikat-pink/8 rounded-2xl border border-seikat-pink/20">
        <div>
          <p className="font-body text-[10px] tracking-widest uppercase text-seikat-pink">Total Transaksi POS</p>
          <p className="font-heading text-3xl text-[#1C1A19]">{orders.length}</p>
        </div>
        <div className="w-px h-12 bg-seikat-pink/20" />
        <div>
          <p className="font-body text-[10px] tracking-widest uppercase text-seikat-pink">Total Pendapatan</p>
          <p className="font-heading text-3xl text-[#1C1A19]">{fmt(totalRevenue)}</p>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-[#D4859B]/12 shadow-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#D4859B]/12 bg-[#FDF8F6]">
              {["ID Transaksi", "Pelanggan", "Items", "Total", "Pembayaran", "Waktu"].map(h => (
                <th key={h} className="text-left px-4 py-3 font-body text-[10px] tracking-widest uppercase text-[#A8A29A]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-16 font-body text-[#A8A29A]">
                <History size={28} className="mx-auto mb-2 text-[#D4859B]/30" />
                <p>Belum ada transaksi POS</p>
              </td></tr>
            ) : orders.map((o, i) => (
              <tr key={o.pos_order_id} className={`border-b border-[#D4859B]/8 hover:bg-[#FDF8F6] transition-colors ${i % 2 === 0 ? "" : "bg-[#FDFAF8]"}`}>
                <td className="px-4 py-3 font-mono text-xs text-[#4A453F]">{o.pos_order_id}</td>
                <td className="px-4 py-3 font-body text-sm text-[#1C1A19]">{o.customer_name || "Walk-in"}</td>
                <td className="px-4 py-3 font-body text-xs text-[#6A6560]">{o.items?.length || 0} item</td>
                <td className="px-4 py-3 font-body text-sm font-semibold text-[#1C1A19]">{fmt(o.total)}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full border text-[10px] font-body uppercase ${
                    o.payment_method === "cash" ? "bg-green-50 text-green-700 border-green-200" :
                    o.payment_method === "card" ? "bg-blue-50 text-blue-700 border-blue-200" :
                    "bg-purple-50 text-purple-700 border-purple-200"
                  }`}>{o.payment_method === "cash" ? "Tunai" : o.payment_method === "card" ? "Kartu" : "Transfer"}</span>
                </td>
                <td className="px-4 py-3 font-body text-xs text-[#8A857D]">
                  {o.created_at ? new Date(o.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
